import api from "./api";
import { getConsumerInfo } from "../controllers/userController";
import { formatReportDateTime } from "./energyReportData";

/** Same parser as HistoricalTab.js */
function parsePriceNumber(value) {
  if (value == null || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Rates from GET /get_customer_info — same source as Info tab / HistoricalTab
 * clientProfilePricing state.
 */
export async function fetchReportPricing(username) {
  if (!username) {
    throw new Error("Missing username.");
  }

  const data = await getConsumerInfo(username);
  return {
    unit_price: data.unit_price,
    gen_unitprice: data.gen_unitprice ?? data.gen_unitPrice ?? null,
    solar_unitprice: data.solar_unitprice ?? data.solar_unitPrice ?? null,
  };
}

/**
 * Mirrors HistoricalTab.js unitPrice useMemo:
 * GENERATOR → gen_unitprice, else → unit_price (solar_unitprice is not used in cost).
 */
export function resolveUnitPrice(category, clientProfilePricing) {
  const normalizedCategory = (category || "").toUpperCase();
  const fromDb =
    normalizedCategory === "GENERATOR"
      ? parsePriceNumber(
          clientProfilePricing?.gen_unitprice ??
            clientProfilePricing?.gen_unitPrice,
        )
      : parsePriceNumber(clientProfilePricing?.unit_price);

  if (fromDb != null) return fromDb;

  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    let priceStr = user?.unit_price;
    if (normalizedCategory === "GENERATOR") {
      priceStr = user?.gen_unitPrice ?? user?.gen_unitprice ?? priceStr;
    }
    const price = parseFloat(priceStr);
    return Number.isFinite(price) ? price : 0;
  } catch {
    return 0;
  }
}

/**
 * Mirrors HistoricalTab.js Estimated Cost card (lines ~1731–1739):
 * GENERATOR → EnergyGenerated × rate
 * else → NetEnergy × rate
 */
export function computeEstimatedCost(category, energyValues, unitPrice) {
  const normalizedCategory = (category || "").toUpperCase();
  const rate = Number.isFinite(unitPrice) ? unitPrice : 0;

  if (normalizedCategory === "GENERATOR") {
    const energyGenerated = Number(energyValues?.EnergyGenerated ?? energyValues?.generated ?? 0);
    return energyGenerated * rate;
  }

  const netEnergy = Number(energyValues?.NetEnergy ?? energyValues?.net ?? 0);
  return netEnergy * rate;
}

/**
 * Scope-level summary from GET /summary — same endpoint HistoricalTab uses
 * for EnergyConsumed / EnergyGenerated / NetEnergy on the summary cards.
 */
export async function fetchScopeSummary(username, dateRange, scopeNodeName) {
  if (!username || !dateRange?.start || !dateRange?.end) {
    throw new Error("Missing username or date range.");
  }

  const machineName =
    !scopeNodeName || scopeNodeName === "Usage" ? "Usage" : scopeNodeName;

  const response = await api.get("/summary", {
    params: {
      username,
      machine_name: machineName,
      start: formatReportDateTime(dateRange.start),
      end: formatReportDateTime(dateRange.end),
      metric: "power_kw",
      mode: "daily",
    },
  });

  return response.data;
}

export function buildCostRows(breakdownRows, pricing) {
  return (breakdownRows || []).map((row) => {
    const unitPrice = resolveUnitPrice(row.category, pricing);
    const isGenerator = (row.category || "").toUpperCase() === "GENERATOR";
    const energyBasis = isGenerator ? row.generated : row.net;
    const energyBasisLabel = isGenerator ? "Generated (kWh)" : "Net (kWh)";
    const estimatedCost = computeEstimatedCost(
      row.category,
      { generated: row.generated, net: row.net },
      unitPrice,
    );

    return {
      ...row,
      unitPrice,
      energyBasis,
      energyBasisLabel,
      estimatedCost,
    };
  });
}

export function buildScopeCostSummary(scopeCategory, summaryData, pricing) {
  const unitPrice = resolveUnitPrice(scopeCategory, pricing);
  const isGenerator = (scopeCategory || "").toUpperCase() === "GENERATOR";
  const energyBasis = isGenerator
    ? Number(summaryData?.EnergyGenerated ?? 0)
    : Number(summaryData?.NetEnergy ?? 0);
  const energyBasisLabel = isGenerator ? "Generated (kWh)" : "Net (kWh)";
  const estimatedCost = computeEstimatedCost(scopeCategory, summaryData, unitPrice);

  return {
    unitPrice,
    energyBasis,
    energyBasisLabel,
    estimatedCost,
    rateLabel:
      isGenerator ? "Generator rate (PKR/kWh)" : "Grid rate (PKR/kWh)",
  };
}
